import dayjs, { Dayjs } from 'dayjs';

export const capitalizeString = (str: string | null | undefined): string =>
  str ? str.charAt(0).toUpperCase() + str.slice(1) : '';

/** Groups of four, the way an Aadhaar number is printed on the card. */
const inGroupsOfFour = (value: string): string => value.replace(/(.{4})(?=.)/g, '$1 ');

/**
 * The digits of an Aadhaar number, with any formatting dropped - legacy records hold it
 * spaced, `8255 8269 1519`, which is not what the twelve-digit validation expects.
 */
export const aadhaarDigits = (value: string | number | null | undefined): string =>
  String(value ?? '').replace(/\D/g, '');

/** An Aadhaar number spaced for reading: `2345 6789 0123`. */
export const formatAadhaar = (value: string | number | null | undefined): string => {
  const digits = aadhaarDigits(value);
  if (!digits) return '';
  return inGroupsOfFour(digits);
};

/**
 * An Aadhaar number with everything but its last four digits hidden - the masking UIDAI
 * asks for, so a full number is never left sitting on a screen someone can read over.
 *
 * Anything that is not twelve digits is masked the same way rather than being passed
 * through: a stored value in an unexpected shape is still an identity number.
 */
export const maskAadhaar = (value: string | number | null | undefined): string => {
  const digits = aadhaarDigits(value);
  if (!digits) return '';
  if (digits.length <= 4) return inGroupsOfFour(digits);
  // Grouped after masking, not before - the X's are part of the string being spaced out.
  return inGroupsOfFour('X'.repeat(digits.length - 4) + digits.slice(-4));
};

export const normalizeToArray = <T>(value: T | T[] | null | undefined): T[] => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

export const strToNumber = (str: string | number | null | undefined): number => {
  if (!str) return 0;
  return Number(str.toString().replace(/₹/g, '').replace(/,/g, ''));
};

export function limitToTwoDecimals(value: number | string): number {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100) / 100;
}

export const formatNumber = (num: number | string | null | undefined): string => {
  if (!num || num === '' || num === '0') return '0';
  let numToFormat = num.toString().replace(/₹/g, '').replace(/,/g, '');
  const parsed = parseFloat(numToFormat);
  if (isNaN(parsed)) return '0';
  return new Intl.NumberFormat('en-IN').format(parsed);
};

export const formatAmount = (amount: string | number | null | undefined): string => {
  if (!amount || amount === '' || amount === '0') return '₹0';
  const amountStr = amount.toString().replace(/₹/g, '').replace(/\.00|,/g, '');
  const num = Number(amountStr);
  if (isNaN(num) || num < 1000) return `₹${amountStr}`;
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(num);
};

export const formatDate = (date: string | Date | Dayjs | null | undefined, format?: string): string => {
  if (!date) return '';
  return dayjs(date as any).format(format || 'DD/MM/YYYY');
};

export const formatDateTime = (date: string | Date | Dayjs | null | undefined, format?: string): string => {
  if (!date) return '';
  return dayjs(date as any).format(format || 'DD/MM/YYYY HH:mm');
};

export function ensureArray<T>(val: T | T[] | null | undefined): T[] {
  if (!val) return [];
  return Array.isArray(val) ? val : [val];
}

export const formatToday = (format?: string): string => {
  if (format) return dayjs().format(format);
  return new Date().toISOString().split('T')[0];
};

export function calculateAge(dateOfBirth: string | Date | null | undefined): number | string {
  if (!dateOfBirth) return '';
  const dob = new Date(dateOfBirth);
  if (isNaN(dob.getTime())) return '';
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const m = today.getMonth() - dob.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < dob.getDate())) age--;
  return age;
}

export function extractMonth(date: string | Date | null | undefined): number | null {
  if (!date) return null;
  const d = new Date(date);
  return isNaN(d.getTime()) ? null : d.getMonth() + 1;
}

export function extractYear(date: string | Date | null | undefined): number | null {
  if (!date) return null;
  const d = new Date(date);
  return isNaN(d.getTime()) ? null : d.getFullYear();
}

export function formatTimeString(timeStr: string | null | undefined): string {
  if (!timeStr) return '00:00';
  return timeStr;
}

export function splitMinutes(totalMinutes: number): { hours: number; minutes: number } {
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return { hours, minutes };
}

export function splitMinutesFromTimeString(timeStr: string | null | undefined): { hours: number; minutes: number } {
  if (!timeStr) return { hours: 0, minutes: 0 };
  const [h, m] = timeStr.split(':').map(Number);
  return { hours: h || 0, minutes: m || 0 };
}

export function combineToMinutes(hours: number, minutes: number): number {
  return hours * 60 + minutes;
}

export function basicSort<T extends Record<string, any>>(arr: T[], key: string, direction: 'asc' | 'desc' = 'asc'): T[] {
  return [...arr].sort((a, b) => {
    if (a[key] < b[key]) return direction === 'asc' ? -1 : 1;
    if (a[key] > b[key]) return direction === 'asc' ? 1 : -1;
    return 0;
  });
}

export function getUpdatedSortConfig(currentConfig: { key: string; direction: 'asc' | 'desc' }, key: string): { key: string; direction: 'asc' | 'desc' } {
  if (currentConfig.key === key) {
    return { key, direction: currentConfig.direction === 'asc' ? 'desc' : 'asc' };
  }
  return { key, direction: 'asc' };
}

// Additional utility functions referenced throughout the codebase
